import { useEffect, useState } from "react";
import {
    Container,
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Chip,
    Button,
    ThemeProvider,
    TextField,
    useMediaQuery,
    Tabs,
    Tab,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import SettingsIcon from "@mui/icons-material/Settings";
import NavBar from "../../layout/NavBar";
import { darkTheme } from "../../../theme/darkTheme";
import { UserApi } from "../../../services/api";
import { register, unregister } from '../../../serviceWorkerRegistration';
import { NotificationService } from '../../../services/notificationService';
import { useAuth } from '../../context/AuthContext';
import Notification from "../../util/notifications/Notification";
import NavBarBot from "../../layout/NavBarBot";
import LoadingPage from "../../layout/LoadingPage";
import { calculateAge, getGeschlechtLabel } from "./components/profil/profilUtils";
import ProfilTab from "./components/profil/ProfilTab";
import EinstellungenTab from "./components/profil/EinstellungenTab";
import ConfirmDialog from "./components/profil/ConfirmDialog";
import ChangePasswordDialog from "./components/profil/ChangePasswordDialog";

function ProfilePage() {
    const isMobile = useMediaQuery(darkTheme.breakpoints.down("md"));
    const { nutzer } = useAuth();
    const [, setShowNotification] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        vname: "",
        nname: "",
        email: "",
        geb_datum: "",
        geschlecht: "",
        gewicht: "",
        start_gewicht: "",
        ziel_gewicht: "",
        groesse: "",
    });

    const [changePasswordDialog, setChangePasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Settings States
    const [swEnabled, setSwEnabled] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [pushSubscribed, setPushSubscribed] = useState(false);
    const [swStatus, setSwStatus] = useState('unknown');
    const [cacheSize, setCacheSize] = useState(0);
    const [swInfo, setSwInfo] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });
    const [message, setMessage] = useState({ type: null, text: '' });

    const id = nutzer?.id;

    useEffect(() => {
        fetchUserData();
        checkSWStatus();
        checkNotificationPermission();
        checkPushSubscription();
        getCacheSize();
    }, [id]);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ type: null, text: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const response = await UserApi.getNutzerById(id);
            setUserData(response.data);
            const gebDatum = response.data.geb_datum ? response.data.geb_datum.split("T")[0] : "";
            setFormData({
                vname: response.data.vname || "",
                nname: response.data.nname || "",
                email: response.data.email || "",
                geb_datum: gebDatum,
                geschlecht: response.data.geschlecht || "",
                gewicht: response.data.gewicht || "",
                start_gewicht: response.data.start_gewicht || "",
                ziel_gewicht: response.data.ziel_gewicht || "",
                groesse: response.data.groesse || "",
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Settings Functions
    const checkSWStatus = async () => {
        try {
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                if (registrations.length > 0) {
                    setSwEnabled(true);
                    setSwStatus('registered');
                    const reg = registrations[0];
                    setSwInfo({
                        scope: reg.scope,
                        active: !!reg.active,
                        installing: !!reg.installing,
                        waiting: !!reg.waiting,
                    });
                } else {
                    setSwEnabled(false);
                    setSwStatus('unregistered');
                }
            } else {
                setSwStatus('error');
            }
        } catch (error) {
            console.error('Fehler beim Prüfen des Service Workers:', error);
            setSwStatus('error');
        }
    };

    const checkNotificationPermission = async () => {
        if ('Notification' in window) {
            setNotificationsEnabled(Notification.permission === 'granted');
        }
    };

    const checkPushSubscription = async () => {
        const isSubscribed = await NotificationService.isPushSubscribed();
        setPushSubscribed(isSubscribed);
    };

    const getCacheSize = async () => {
        try {
            if ('storage' in navigator && 'estimate' in navigator.storage) {
                const estimate = await navigator.storage.estimate();
                setCacheSize(estimate.usage);
            }
        } catch (error) {
            console.error('Fehler beim Abrufen der Cache-Größe:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        const dataToSave = {
            ...formData,
            geb_datum: formData.geb_datum ? formData.geb_datum.split("T")[0] : null,
        };
        console.log(dataToSave)
        try {
            setLoading(true);
            await UserApi.updateNutzer(id, dataToSave);
            setEditMode(false);
            setUserData(formData);
            setMessage({ type: 'success', text: 'Profil erfolgreich aktualisiert' });
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Fehler beim Aktualisieren des Profils' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const gebDatum = userData.geb_datum ? userData.geb_datum.split("T")[0] : "";
        setFormData({
            vname: userData.vname || "",
            nname: userData.nname || "",
            email: userData.email || "",
            geb_datum: gebDatum,
            geschlecht: userData.geschlecht || "",
            gewicht: userData.gewicht || "",
            start_gewicht: userData.start_gewicht || "",
            ziel_gewicht: userData.ziel_gewicht || "",
            groesse: userData.groesse || "",
        });
        setEditMode(false);
    };

    const handleSWToggle = (event) => {
        const isChecked = event.target.checked;
        if (event.target) event.target.blur();
        setTimeout(() => {
            if (isChecked && !swEnabled) {
                setConfirmDialog({ open: true, type: 'enable' });
            } else if (!isChecked && swEnabled) {
                setConfirmDialog({ open: true, type: 'disable' });
            }
        }, 50);
    };

    const handleNotificationToggle = (event) => {
        if (event.target) event.target.blur();
        setTimeout(() => {
            if (!notificationsEnabled) {
                setConfirmDialog({ open: true, type: 'notifications' });
            }
        }, 50);
    };

    const handlePushToggle = (event) => {
        if (event.target) event.target.blur();
        setTimeout(() => {
            if (pushSubscribed) {
                setConfirmDialog({ open: true, type: 'pushUnsubscribe' });
            } else {
                setConfirmDialog({ open: true, type: 'pushSubscribe' });
            }
        }, 50);
    };

    const confirmAction = async () => {
        const { type } = confirmDialog;
        setConfirmDialog({ open: false, type: null });
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
            if (type === 'enable') {
                register({
                    onSuccess: () => {
                        setSwEnabled(true);
                        setSwStatus('registered');
                        setMessage({ type: 'success', text: 'Service Worker erfolgreich aktiviert' });
                        checkSWStatus();
                    },
                    onUpdate: () => {
                        setMessage({ type: 'info', text: 'Service Worker Update verfügbar' });
                    },
                });
            } else if (type === 'disable') {
                unregister();
                setSwEnabled(false);
                setSwStatus('unregistered');
                setSwInfo(null);
                setMessage({ type: 'success', text: 'Service Worker deaktiviert' });
            } else if (type === 'clearCache') {
                await clearAllCaches();
                setMessage({ type: 'success', text: 'Cache geleert' });
                getCacheSize();
            } else if (type === 'notifications') {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {
                    setNotificationsEnabled(true);
                    setMessage({ type: 'success', text: 'Benachrichtigungen aktiviert' });
                } else {
                    setMessage({ type: 'error', text: 'Benachrichtigungen wurden abgelehnt' });
                }
            } else if (type === 'pushSubscribe') {
                if (!nutzer?.id) {
                    setMessage({ type: 'error', text: 'Bitte melde dich an' });
                    return;
                }
                const hasPermission = await NotificationService.requestPermission();
                if (!hasPermission) {
                    setMessage({ type: 'error', text: 'Benachrichtigungs-Berechtigung erforderlich' });
                    return;
                }
                const success = await NotificationService.initialize(nutzer.id);
                if (success) {
                    setPushSubscribed(true);
                    setMessage({ type: 'success', text: 'Push-Benachrichtigungen aktiviert' });
                } else {
                    setMessage({ type: 'error', text: 'Fehler beim Aktivieren der Push-Benachrichtigungen' });
                }
            } else if (type === 'pushUnsubscribe') {
                if (!nutzer?.id) {
                    setMessage({ type: 'error', text: 'Bitte melde dich an' });
                    return;
                }
                const success = await NotificationService.unsubscribeFromPush(nutzer.id);
                if (success) {
                    setPushSubscribed(false);
                    setMessage({ type: 'success', text: 'Push-Benachrichtigungen deaktiviert' });
                } else {
                    setMessage({ type: 'error', text: 'Fehler beim Deaktivieren der Push-Benachrichtigungen' });
                }
            }
        } catch (error) {
            console.error('Fehler bei Aktion:', error);
            setMessage({ type: 'error', text: `Fehler: ${error.message}` });
        }
    };

    const clearAllCaches = async () => {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }
    };

    const handleUpdateSW = async () => {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            registrations.forEach((reg) => reg.update());
            setMessage({ type: 'info', text: 'Prüfe auf Updates...' });
        }
    };

    const handleChangePassword = async () => {
        if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'Alle Felder müssen ausgefüllt sein' });
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setMessage({ type: 'error', text: 'Passwort muss mindestens 6 Zeichen lang sein' });
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'Neue Passwörter stimmen nicht überein' });
            return;
        }

        setPasswordLoading(true);

        try {
            await UserApi.changePassword(id, {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword
            });
            setMessage({ type: 'success', text: 'Passwort erfolgreich geändert' });
            setChangePasswordDialog(false);
            setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            if (err.response?.data?.error) {
                setMessage({ type: 'error', text: err.response.data.error });
            } else {
                setMessage({ type: 'error', text: 'Fehler beim Ändern des Passworts' });
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loading && !userData) {
        return (
            <LoadingPage />
        );
    }

    return (
        <>
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: { xs: 2, md: 4 }, mb: "54px" }}>
                    <NavBar />
                    <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 } }}>
                        {message.text && (
                            <Notification
                                type={message.type}
                                message={message.text}
                                onClose={() => {
                                    setShowNotification(false);
                                    setMessage({ type: null, text: '' });
                                }}
                            />
                        )}

                        {/* Profil Header Card */}
                        <Card sx={{ mb: { xs: 2, md: 4 }, borderRadius: '16px', boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)" }}>
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <Box display="flex" flexDirection={{ xs: "column", md: "row" }} alignItems={{ xs: "center", md: "flex-start" }} gap={{ xs: 2, md: 3 }}>
                                    <Avatar sx={{ width: { xs: 100, md: 140 }, height: { xs: 100, md: 140 }, bgcolor: "linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)", fontSize: { xs: "2.5rem", md: "3.5rem" }, border: "3px solid #1e293b" }}>
                                        {formData?.vname?.charAt(0)}{formData?.nname?.charAt(0)}
                                    </Avatar>
                                    <Box flex={1} textAlign={{ xs: "center", md: "left" }}>
                                        {editMode ? (
                                            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={{ xs: 1, md: 2 }} mb={{ xs: 1, md: 2 }}>
                                                <TextField name="vname" label="Vorname" value={formData.vname} onChange={handleInputChange} size="small" fullWidth />
                                                <TextField name="nname" label="Nachname" value={formData.nname} onChange={handleInputChange} size="small" fullWidth />
                                            </Box>
                                        ) : (
                                            <Typography variant={{ xs: "h5", md: "h3" }} fontWeight={700} gutterBottom sx={{ color: "#e0f2fe" }}>
                                                {formData?.vname} {formData?.nname}
                                            </Typography>
                                        )}
                                        <Box display="flex" gap={{ xs: 1, md: 1.5 }} flexWrap="wrap" justifyContent={{ xs: "center", md: "flex-start" }} sx={{ mt: { xs: 1, md: 1 } }}>
                                            {formData?.geschlecht && (
                                                <Chip label={getGeschlechtLabel(formData.geschlecht)} size="medium" sx={{ bgcolor: "rgba(59, 130, 246, 0.2)", color: "#93c5fd", fontWeight: 500, borderRadius: "16px" }} />
                                            )}
                                            {formData?.geb_datum && (
                                                <Chip label={`${calculateAge(formData.geb_datum)} Jahre`} size="medium" variant="outlined" sx={{ color: "#93c5fd", borderColor: "#3b82f6", fontWeight: 500, borderRadius: "16px" }} />
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1 }, mt: { xs: 2, md: 0 }, width: { xs: "100%", md: "auto" } }}>
                                        {editMode ? (
                                            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={1} width="100%">
                                                <Button variant="contained" color="success" startIcon={<SaveIcon />} onClick={handleSave} sx={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", borderRadius: '16px', padding: "8px 16px", textTransform: "none", width: { xs: "100%", md: "auto" } }}>
                                                    Speichern
                                                </Button>
                                                <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel} sx={{ color: "#f87171", borderColor: "#f87171", borderRadius: '16px', padding: "8px 16px", textTransform: "none", width: { xs: "100%", md: "auto" } }}>
                                                    Abbrechen
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditMode(true)} sx={{ background: "linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)", borderRadius: '16px', padding: "8px 16px", textTransform: "none", width: { xs: "100%", md: "auto" } }}>
                                                Bearbeiten
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Tabs */}
                        <Card sx={{ mb: 3, borderRadius: '16px' }}>
                            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tab icon={<PersonIcon />} label="Profil" iconPosition="start" />
                                <Tab icon={<SettingsIcon />} label="Einstellungen" iconPosition="start" />
                            </Tabs>
                        </Card>

                        {/* Profil Tab */}
                        {currentTab === 0 && (
                            <ProfilTab
                                editMode={editMode}
                                formData={formData}
                                onInputChange={handleInputChange}
                                isMobile={isMobile}
                            />
                        )}

                        {/* Settings Tab */}
                        {currentTab === 1 && (
                            <EinstellungenTab
                                swEnabled={swEnabled}
                                swStatus={swStatus}
                                swInfo={swInfo}
                                notificationsEnabled={notificationsEnabled}
                                pushSubscribed={pushSubscribed}
                                cacheSize={cacheSize}
                                onSWToggle={handleSWToggle}
                                onNotificationToggle={handleNotificationToggle}
                                onPushToggle={handlePushToggle}
                                onUpdateSW={handleUpdateSW}
                                onChangePasswordOpen={() => setChangePasswordDialog(true)}
                                onDisableSW={() => setConfirmDialog({ open: true, type: 'disable' })}
                                onClearCache={() => setConfirmDialog({ open: true, type: 'clearCache' })}
                            />
                        )}
                    </Container>

                    <ConfirmDialog
                        open={confirmDialog.open}
                        type={confirmDialog.type}
                        onClose={() => setConfirmDialog({ open: false, type: null })}
                        onConfirm={confirmAction}
                    />

                    <ChangePasswordDialog
                        open={changePasswordDialog}
                        onClose={() => setChangePasswordDialog(false)}
                        passwordData={passwordData}
                        onPasswordData={setPasswordData}
                        loading={passwordLoading}
                        onSubmit={handleChangePassword}
                    />
                </Box>
            </ThemeProvider>
            <NavBarBot />
        </>
    );
}

export default ProfilePage;
